export const PRIVACY_POLICY_VERSION = "2.0";

export function PrivacyPolicyContent() {
  return (
    <div className="prose prose-sm max-w-none dark:prose-invert">
      <p className="text-muted-foreground mb-6">
        <strong>Last Updated:</strong> December 2025
      </p>

      <p className="mb-6">
        Wellness Connect  Pty Ltd ABN [ABN Number] ("we", "us", "our") is committed to protecting 
        your privacy and complying with the Privacy Act 1988 (Cth) and the Privacy Principles (APPs).
      </p>

      <h3 className="text-lg font-semibold mt-6 mb-3">1. Scope of This Policy</h3>
      <p>
        This Privacy Policy applies to all personal information collected by Wellness Connect  
        through our website, mobile application, and related services (collectively, the "Platform"). 
        It explains how we collect, use, disclose, and protect your personal information.
      </p>

      <h3 className="text-lg font-semibold mt-6 mb-3">2. Personal Information We Collect</h3>
      <p>We may collect the following types of personal information:</p>
      
      <h4 className="font-medium mt-4 mb-2">A. Information You Provide Directly</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>Identity information: name, date of birth, gender</li>
        <li>Contact information: email address, phone number, postal address</li>
        <li>Account credentials: username, password (encrypted)</li>
        <li>Profile information: profile photo, preferences, health-related information you choose to share</li>
        <li>Business information (for service providers): ABN, business name, qualifications, certifications</li>
        <li>Payment information: billing address (payment card details are processed securely by our payment processor and not stored by us)</li>
      </ul>

      <h4 className="font-medium mt-4 mb-2">B. Information Collected Automatically</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>Device information: IP address, browser type, operating system</li>
        <li>Usage data: pages visited, features used, booking history</li>
        <li>Location data: general location based on IP address, or precise location if you enable location services</li>
        <li>Cookies and similar technologies: session cookies, analytics cookies</li>
      </ul>

      <h4 className="font-medium mt-4 mb-2">C. Information from Third Parties</h4>
      <ul className="list-disc pl-6 space-y-1">
        <li>Social login providers (if you choose to sign in via Google): name, email address</li>
        <li>Payment processors: transaction confirmation details</li>
        <li>Business verification services: ABN validation</li>
      </ul>

      <h4 className="font-medium mt-4 mb-2">D. Sensitive Information</h4>
      <p>
        We only collect sensitive information (such as health information) with your explicit consent 
        and where it is reasonably necessary for the services you request. You may choose to share 
        health-related preferences or conditions to help service providers better assist you.
      </p>

      <h3 className="text-lg font-semibold mt-6 mb-3">3. Purpose of Collection and Use</h3>
      <p>We collect and use your personal information for the following purposes:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>To provide, maintain, and improve our Platform and services</li>
        <li>To process bookings and facilitate connections between customers and service providers</li>
        <li>To process payments and send transaction confirmations</li>
        <li>To verify service provider credentials and qualifications</li>
        <li>To communicate with you about your account, bookings, and service updates</li>
        <li>To send marketing communications (with your consent)</li>
        <li>To respond to your enquiries and provide customer support</li>
        <li>To detect, prevent, and address fraud, security issues, and technical problems</li>
        <li>To comply with legal obligations and enforce our terms of service</li>
        <li>To analyse usage patterns and improve user experience</li>
      </ul>

      <h3 className="text-lg font-semibold mt-6 mb-3">4. Legal Basis for Processing</h3>
      <p>We process your personal information based on the following legal grounds:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Contract:</strong> Processing necessary for the performance of our agreement with you</li>
        <li><strong>Consent:</strong> Where you have given explicit consent for specific purposes</li>
        <li><strong>Legitimate Interests:</strong> For our legitimate business interests, such as improving our services</li>
        <li><strong>Legal Obligation:</strong> Where processing is required by applicable law</li>
      </ul>

      <h3 className="text-lg font-semibold mt-6 mb-3">5. Disclosure of Personal Information</h3>
      <p>We may disclose your personal information to:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Service Providers on our Platform:</strong> When you make a booking, relevant information is shared with the service provider to fulfil your booking</li>
        <li><strong>Customers (for service providers):</strong> Your business profile information is visible to customers browsing the Platform</li>
        <li><strong>Third-party service providers:</strong> Including cloud hosting providers, email service providers, analytics providers, and payment gateways</li>
        <li><strong>Professional advisers:</strong> Including lawyers, accountants, and auditors</li>
        <li><strong>Government authorities:</strong> Where required by law or to protect our legal rights</li>
      </ul>
      <p className="mt-2">
        We require all third parties to maintain the confidentiality and security of your personal 
        information and to use it only for the purposes for which it was disclosed.
      </p>

      <h3 className="text-lg font-semibold mt-6 mb-3">6. Overseas Disclosure</h3>
      <p>
        Some of our third-party service providers may be located overseas, including in the United States 
        and the European Union. Before disclosing your personal information overseas, we take reasonable 
        steps to ensure the recipient handles your information in accordance with the APPs or is subject 
        to a substantially similar privacy regime.
      </p>

      <h3 className="text-lg font-semibold mt-6 mb-3">7. Data Retention</h3>
      <p>
        We retain your personal information for as long as necessary to fulfil the purposes for which 
        it was collected, including to satisfy legal, accounting, or reporting requirements. The retention 
        period may vary depending on the context and our legal obligations.
      </p>
      <p className="mt-2">When you delete your account:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Your personal profile information is deleted within 30 days</li>
        <li>Transaction records may be retained for up to 7 years for legal and tax purposes</li>
        <li>Anonymised data may be retained for analytics purposes</li>
      </ul>

      <h3 className="text-lg font-semibold mt-6 mb-3">8. Data Security</h3>
      <p>
        We implement appropriate technical and organisational measures to protect your personal information, 
        including:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Encryption of data in transit (TLS/SSL) and at rest</li>
        <li>Secure password hashing</li>
        <li>Regular security assessments and updates</li>
        <li>Access controls and authentication measures</li>
        <li>Employee training on data protection</li>
        <li>Incident response procedures</li>
      </ul>
      <p className="mt-2">
        However, no method of transmission over the Internet or electronic storage is 100% secure. 
        While we strive to protect your personal information, we cannot guarantee absolute security.
      </p>

      <h3 className="text-lg font-semibold mt-6 mb-3">9. Your Rights</h3>
      <p>Under the Privacy Act, you have the right to:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
        <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
        <li><strong>Erasure:</strong> Request deletion of your personal information (subject to legal requirements)</li>
        <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
        <li><strong>Complaint:</strong> Lodge a complaint about our handling of your personal information</li>
      </ul>
      <p className="mt-2">
        To exercise these rights, please contact us using the details provided below. We will respond 
        to your request within a reasonable timeframe, generally within 30 days.
      </p>

      <h3 className="text-lg font-semibold mt-6 mb-3">10. Data Breach Notification</h3>
      <p>
        In the event of a data breach that is likely to result in serious harm to you, we will notify 
        you and the Office of the Information Commissioner (OAIC) as required by the Notifiable 
        Data Breaches scheme under the Privacy Act.
      </p>

      <h3 className="text-lg font-semibold mt-6 mb-3">11. Cookies and Tracking Technologies</h3>
      <p>We use cookies and similar technologies to:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Remember your preferences and settings</li>
        <li>Authenticate your identity and maintain your session</li>
        <li>Analyse how our Platform is used</li>
        <li>Deliver relevant content and advertisements</li>
      </ul>
      <p className="mt-2">
        You can control cookies through your browser settings. However, disabling certain cookies may 
        affect the functionality of our Platform.
      </p>

      <h3 className="text-lg font-semibold mt-6 mb-3">12. Third-Party Links</h3>
      <p>
        Our Platform may contain links to third-party websites or services. We are not responsible for 
        the privacy practices of these third parties. We encourage you to read the privacy policies of 
        any third-party sites you visit.
      </p>

      <h3 className="text-lg font-semibold mt-6 mb-3">13. Changes to This Privacy Policy</h3>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of any material changes 
        by posting the updated policy on our Platform and, where appropriate, by email. Your continued 
        use of our Platform after any changes constitutes your acceptance of the updated policy.
      </p>

      <h3 className="text-lg font-semibold mt-6 mb-3">14. Privacy Officer Contact</h3>
      <p>
        If you have any questions, concerns, or requests regarding this Privacy Policy or our handling 
        of your personal information, please contact our Privacy Officer:
      </p>
      <div className="bg-muted/50 p-4 rounded-lg mt-2">
        <p><strong>Privacy Officer</strong></p>
        <p>Wellness Connect  Pty Ltd</p>
        <p>Email: hello@wellnessconnect.com</p>
        <p>Address: 601 Little Lonsdale Street, </p>
      </div>

      <h3 className="text-lg font-semibold mt-6 mb-3">15. Complaints</h3>
      <p>
        If you are not satisfied with our response to your privacy concern, you may lodge a complaint 
        with the Office of the Information Commissioner (OAIC):
      </p>
      <div className="bg-muted/50 p-4 rounded-lg mt-2">
        <p>Website: <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.oaic.gov.au</a></p>
        <p>Phone: 1300 363 992</p>
        <p>Post: GPO Box 5288, Sydney NSW 2001</p>
      </div>

      <h3 className="text-lg font-semibold mt-6 mb-3">16. Consent</h3>
      <p>
        By using our Platform, you consent to the collection, use, and disclosure of your personal 
        information as described in this Privacy Policy. If you do not agree with this policy, please 
        do not use our Platform.
      </p>
    </div>
  );
}
